import React, { useCallback, useEffect, useState, useRef } from "react";
import { motion, useAnimation } from "framer-motion";

import logo from "assets/img/ncentiva-loader.svg";

export default function Loader(props) {
  const stylesheets = [import("assets/scss/loader.scss")];
  const initAnim = useAnimation();
  const topHalfAnim = useAnimation();
  const bottomHalfAnim = useAnimation();
  const logoAnim = useAnimation();
  const mainLoaderAnim = useAnimation();
  const [lastCalledFrom, setLastCalledFrom] = useState("");
  const hasMounted = useRef(false);

  async function startLoader() {
    if (!hasMounted.current) return; // Prevent running if the component is not mounted
    mainLoaderAnim.set({ display: "block" });
    initAnim.set({ rotateZ: -105, x: "-25%", y: "-25%" });
    topHalfAnim.set({ y: "-100%" });
    bottomHalfAnim.set({ y: "200%" });
    logoAnim.set({ x: "-50%", y: "-50%", scale: 3, opacity: 0 });
    await topHalfAnim.start({ y: "0%", transition: { duration: 0.2, ease: "easeOut" } });
    await bottomHalfAnim.start({ y: "100%", transition: { duration: 0.2, ease: "easeOut" } });
    await logoAnim.start({
      rotateZ: 105,
      x: "-50%",
      y: "-50%",
      scale: 1,
      opacity: 1,
      transition: { duration: 0.15, ease: "easeIn" },
    });
    logoAnim.start({
      rotateZ: 105,
      x: "-50%",
      y: "-50%",
      scale: 1,
      opacity: 1,
      transition: { duration: 0.15, ease: "easeIn" },
    });
    await cbEndLoader();
  }

  async function endLoader() {
    if (!hasMounted.current) return; // Prevent running if the component is not mounted
    logoAnim.stop();
    await initAnim.start({
      rotateZ: 75,
      x: "-25%",
      y: "-25%",
      transition: { duration: 0.2, ease: "easeInOut" },
    });
    await Promise.all([
      topHalfAnim.start({ y: "-100%", transition: { duration: 0.2, ease: "easeOut" } }),
      bottomHalfAnim.start({ y: "200%", transition: { duration: 0.2, ease: "easeOut" } }),
    ]);
    await logoAnim.start({
      rotateZ: -105,
      x: "-50%",
      y: "-50%",
      scale: 3,
      opacity: 0,
      transition: { duration: 0.25, ease: "easeOut" },
    });
    mainLoaderAnim.set({ display: "none" });
  }

  const cbEndLoader = useCallback(endLoader, [
    bottomHalfAnim,
    initAnim,
    logoAnim,
    mainLoaderAnim,
    topHalfAnim,
  ]);
  const cbStartLoader = useCallback(startLoader, [
    bottomHalfAnim,
    cbEndLoader,
    initAnim,
    logoAnim,
    mainLoaderAnim,
    topHalfAnim,
  ]);

  useEffect(() => {
    hasMounted.current = true; // Set mounted flag
    if (props.startLoadingFrom !== lastCalledFrom) {
      setLastCalledFrom(props.startLoadingFrom);
      cbStartLoader();
    }
    return () => {
      hasMounted.current = false; // Clean up on unmount
    };
  }, [props.startLoadingFrom, lastCalledFrom, cbStartLoader, cbEndLoader]);

  return (
    <motion.div
      className="Loader"
      animate={mainLoaderAnim}
      onLoad={props.notOnLoad ? () => null : cbStartLoader}
    >
      <motion.div animate={initAnim} className="mainContainer">
        <motion.div animate={topHalfAnim} className="topHalf" >
          &nbsp;
        </motion.div>
        <motion.div animate={bottomHalfAnim} className="bottomHalf">
          &nbsp;
        </motion.div>
        <motion.div className="logo" animate={logoAnim}>
          <img src={logo} alt="Ncentiva" />
        </motion.div>
      </motion.div>
    </motion.div>
  );
}
